import LocationPicker from "../LocationPicker";
import Select from "react-select";

const FILTER_SECTIONS = [
  {
    id: "basic",
    label: "Basic Search",
    icon: "bi-search",
    fields: [
      "keyword",
      "jobRole",
      "location",
      "latitude",
      "longitude",
      "radiusKm",
    ],
  },
  {
    id: "category",
    label: "Category & Skills",
    icon: "bi-tags",
    fields: ["department", "skill"],
  },
  {
    id: "type",
    label: "Job Type",
    icon: "bi-briefcase",
    fields: ["employmentType", "workMode"],
  },
  {
    id: "experience",
    label: "Experience (yrs)",
    icon: "bi-award",
    fields: ["experienceMin", "experienceMax"],
  },
  {
    id: "salary",
    label: "Salary Range (₹)",
    icon: "bi-currency-rupee",
    fields: ["salaryMin", "salaryMax"],
  },
];

const WORK_MODE_OPTIONS = [
  { value: "", label: "Any Mode", icon: "🌐" },
  { value: "onsite", label: "Onsite", icon: "🏢" },
  { value: "remote", label: "Remote", icon: "🏠" },
  { value: "hybrid", label: "Hybrid", icon: "🔀" },
];

const EMPLOYMENT_OPTIONS = [
  { value: "", label: "Any Type" },
  { value: "full_time", label: "Full Time" },
  { value: "part_time", label: "Part Time" },
  { value: "contract", label: "Contract" },
  { value: "internship", label: "Internship" },
  { value: "temporary", label: "Temporary" },
  { value: "freelance", label: "Freelance" },
];

const RADIUS_OPTIONS = ["5", "10", "25", "50", "100", "200"];

const roleSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 40,
    border: "1.5px solid #e9ecef",
    borderColor: state.isFocused ? "#0d6efd" : "#e9ecef",
    borderRadius: 10,
    background: state.isFocused ? "#fff" : "#f8f9fa",
    boxShadow: state.isFocused ? "0 0 0 3px rgba(13,110,253,0.10)" : "none",
    fontSize: 13.5,
  }),
  menu: (base) => ({ ...base, zIndex: 30, borderRadius: 10, overflow: "hidden" }),
  groupHeading: (base) => ({
    ...base,
    margin: 0,
    padding: "8px 12px",
    background: "#eef2f7",
    color: "#344054",
    fontSize: 12,
    fontWeight: 700,
    textTransform: "none",
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 13.5,
    background: state.isSelected
      ? "#e7f0ff"
      : state.isFocused
      ? "#f5f8ff"
      : "#fff",
    color: "#212529",
  }),
};

const hasValue = (filters, fields) =>
  fields.some((f) => filters[f] !== "" && filters[f] !== undefined);

const ActiveBadge = () => (
  <span
    style={{
      width: 7,
      height: 7,
      borderRadius: "50%",
      background: "#0d6efd",
      display: "inline-block",
      marginLeft: 6,
      verticalAlign: "middle",
    }}
  />
);

const JobFiltersPanel = ({
  filters,
  onChange,
  onSubmit,
  onReset,
  categories = [],
  jobRoles = [],
  loading = false,
}) => {
  const activeCount = Object.entries(filters).filter(
    ([k, v]) => k !== "sort" && v !== ""
  ).length;

  const handleNearbyLocation = (location) => {
    onChange("location", location.address || "");
    onChange("latitude", location.latitude || "");
    onChange("longitude", location.longitude || "");
    onChange("radiusKm", filters.radiusKm || "25");
  };

  const jobRoleOptions = (() => {
    const groupedRoles = new Map();

    (Array.isArray(jobRoles) ? jobRoles : []).forEach((item) => {
      const groupLabel = item.jobRoleCategory || "Suggested Job Roles";
      const option = {
        value: item.name,
        label: item.name,
      };

      if (!groupedRoles.has(groupLabel)) {
        groupedRoles.set(groupLabel, []);
      }

      groupedRoles.get(groupLabel).push(option);
    });

    return Array.from(groupedRoles, ([label, options]) => ({ label, options }));
  })();

  const selectedJobRoleOption =
    jobRoleOptions
      .flatMap((group) => group.options)
      .find((option) => option.value === filters.jobRole) || null;

  return (
    <>
      <style>{`
        .filter-panel {
          border-radius: 16px;
          border: 1px solid #e9ecef;
          background: #fff;
          box-shadow: 0 2px 12px rgba(0,0,0,0.06);
          overflow: hidden;
        }
        .filter-panel-header {
          background: linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%);
          padding: 18px 20px;
          color: #fff;
        }
        .filter-section {
          border-bottom: 1px solid #f1f3f5;
          padding: 14px 20px;
        }
        .filter-section:last-of-type { border-bottom: none; }
        .filter-section-label {
          font-size: 11px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.8px;
          color: #000000;
          margin-bottom: 10px;
          display: flex;
          align-items: center;
          gap: 6px;
        }
        .filter-input {
          border: 1.5px solid #e9ecef;
          border-radius: 10px;
          padding: 8px 12px;
          font-size: 13.5px;
          width: 100%;
          transition: border-color 0.15s, box-shadow 0.15s;
          outline: none;
          background: #f8f9fa;
          color: #212529;
        }
        .filter-input:focus {
          border-color: #0d6efd;
          background: #fff;
          box-shadow: 0 0 0 3px rgba(13,110,253,0.10);
        }
        .filter-input::placeholder { color: #ced4da; }
        .filter-select {
          border: 1.5px solid #e9ecef;
          border-radius: 10px;
          padding: 8px 12px;
          font-size: 13.5px;
          width: 100%;
          background: #f8f9fa;
          color: #212529;
          cursor: pointer;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 16 16'%3E%3Cpath fill='%23adb5bd' d='M7.247 11.14L2.451 5.658C1.885 5.013 2.345 4 3.204 4h9.592a1 1 0 0 1 .753 1.659l-4.796 5.48a1 1 0 0 1-1.506 0z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          padding-right: 32px;
          transition: border-color 0.15s, box-shadow 0.15s;
          outline: none;
        }
        .filter-select:focus {
          border-color: #0d6efd;
          background-color: #fff;
          box-shadow: 0 0 0 3px rgba(13,110,253,0.10);
        }
        .work-mode-pills { display: flex; gap: 6px; flex-wrap: wrap; }
        .work-mode-pill {
          padding: 5px 12px;
          border-radius: 20px;
          font-size: 12.5px;
          font-weight: 500;
          border: 1.5px solid #dee2e6;
          background: #f8f9fa;
          color: #495057;
          cursor: pointer;
          transition: all 0.15s;
          white-space: nowrap;
        }
        .work-mode-pill:hover { border-color: #0d6efd; color: #0d6efd; background: #e7f1ff; }
        .work-mode-pill.active {
          border-color: #0d6efd;
          background: #0d6efd;
          color: #fff;
        }
        .range-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
        .range-label { font-size: 11px; color: #868e96; margin-bottom: 4px; }
        .filter-apply-btn {
          background: linear-gradient(135deg, #0d6efd, #0a58ca);
          border: none;
          border-radius: 10px;
          color: #fff;
          font-size: 14px;
          font-weight: 600;
          padding: 10px;
          width: 100%;
          cursor: pointer;
          transition: opacity 0.15s, transform 0.1s;
          letter-spacing: 0.2px;
        }
        .filter-apply-btn:hover:not(:disabled) { opacity: 0.9; transform: translateY(-1px); }
        .filter-apply-btn:disabled { opacity: 0.65; cursor: not-allowed; transform: none; }
        .filter-reset-btn {
          background: none;
          border: none;
          font-size: 12px;
          color: rgba(255,255,255,0.8);
          cursor: pointer;
          padding: 0;
          text-decoration: underline;
          transition: color 0.15s;
        }
        .filter-reset-btn:hover { color: #fff; }
        .filter-footer { padding: 14px 20px 18px; }
      `}</style>

      <form onSubmit={onSubmit}>
        <div className="filter-panel">
          {/* Header */}
          <div className="filter-panel-header">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center gap-2">
                <i className="bi bi-funnel-fill" style={{ fontSize: 16 }} />
                <span className="fw-bold" style={{ fontSize: 15 }}>
                  Filters
                </span>
                {activeCount > 0 && (
                  <span
                    className="badge"
                    style={{
                      background: "rgba(255,255,255,0.25)",
                      fontSize: 11,
                      borderRadius: 20,
                      padding: "2px 8px",
                    }}
                  >
                    {activeCount} active
                  </span>
                )}
              </div>
              {activeCount > 0 && (
                <button type="button" className="filter-reset-btn" onClick={onReset}>
                  Clear all
                </button>
              )}
            </div>
          </div>

          {/* Keyword */}
          <div className="filter-section">
            <div className="filter-section-label">
              <i className="bi bi-search" />
              Keyword
              {hasValue(filters, ["keyword"]) && <ActiveBadge />}
            </div>
            <input
              className="filter-input"
              value={filters.keyword}
              onChange={(e) => onChange("keyword", e.target.value)}
              placeholder="Title, company, skill…"
            />
          </div>

          {/* Job Title + Location */}
          <div className="filter-section">
            <div className="filter-section-label">
              <i className="bi bi-person-badge" />
              Role & Location
              {hasValue(filters, ["jobRole", "location"]) && <ActiveBadge />}
            </div>
            <div className="d-flex flex-column gap-2">
              <Select
                isClearable
                isSearchable
                value={selectedJobRoleOption}
                onChange={(option) => onChange("jobRole", option?.value || "")}
                options={jobRoleOptions}
                placeholder="Select Job Role"
                styles={roleSelectStyles}
                noOptionsMessage={() => "No job roles found"}
              />
              <div style={{ position: "relative" }}>
                <i
                  className="bi bi-geo-alt"
                  style={{
                    position: "absolute",
                    left: 11,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#adb5bd",
                    fontSize: 13,
                    pointerEvents: "none",
                  }}
                />
                <input
                  className="filter-input"
                  style={{ paddingLeft: 28 }}
                  value={filters.location}
                  onChange={(e) => {
                    onChange("location", e.target.value);
                    onChange("latitude", "");
                    onChange("longitude", "");
                  }}
                  placeholder="Search job location or remote"
                />
              </div>
              <LocationPicker
                value={filters.location}
                onSelect={handleNearbyLocation}
                label="Search nearby location"
                compact
                helperText="Pick a map result or use current location."
              />
              <select
                className="filter-select"
                value={filters.radiusKm}
                onChange={(e) => onChange("radiusKm", e.target.value)}
                disabled={!filters.latitude || !filters.longitude}
              >
                <option value="">Any distance</option>
                {RADIUS_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    Within {value} km
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Category + Skill */}
          <div className="filter-section">
            <div className="filter-section-label">
              <i className="bi bi-tags" />
              Category & Skills
              {hasValue(filters, ["department", "skill"]) && <ActiveBadge />}
            </div>
            <div className="d-flex flex-column gap-2">
              <select
                className="filter-select"
                value={filters.department}
                onChange={(e) => onChange("department", e.target.value)}
              >
                <option value="">All categories</option>
                {categories.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </select>
              <input
                className="filter-input"
                value={filters.skill}
                onChange={(e) => onChange("skill", e.target.value)}
                placeholder="React, Node.js, Python…"
              />
            </div>
          </div>

          {/* Employment Type */}
          <div className="filter-section">
            <div className="filter-section-label">
              <i className="bi bi-briefcase" />
              Job Type
              {hasValue(filters, ["employmentType"]) && <ActiveBadge />}
            </div>
            <select
              className="filter-select"
              value={filters.employmentType}
              onChange={(e) => onChange("employmentType", e.target.value)}
            >
              {EMPLOYMENT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          {/* Work Mode pills */}
          <div className="filter-section">
            <div className="filter-section-label">
              <i className="bi bi-laptop" />
              Work Mode
              {hasValue(filters, ["workMode"]) && <ActiveBadge />}
            </div>
            <div className="work-mode-pills">
              {WORK_MODE_OPTIONS.map((o) => (
                <button
                  key={o.value}
                  type="button"
                  className={`work-mode-pill ${filters.workMode === o.value ? "active" : ""}`}
                  onClick={() => onChange("workMode", o.value)}
                >
                    {o.label}
                </button>
              ))}
            </div>
          </div>

          {/* Experience */}
          <div className="filter-section">
            <div className="filter-section-label">
              <i className="bi bi-award" />
              Experience (years)
              {hasValue(filters, ["experienceMin", "experienceMax"]) && <ActiveBadge />}
            </div>
            <div className="range-row">
              <div>
                <div className="range-label">Minimum</div>
                <input
                  type="number"
                  min="0"
                  className="filter-input"
                  value={filters.experienceMin}
                  onChange={(e) => onChange("experienceMin", e.target.value)}
                  placeholder="0"
                />
              </div>
              <div>
                <div className="range-label">Maximum</div>
                <input
                  type="number"
                  min="0"
                  className="filter-input"
                  value={filters.experienceMax}
                  onChange={(e) => onChange("experienceMax", e.target.value)}
                  placeholder="10"
                />
              </div>
            </div>
          </div>

          {/* Salary */}
          <div className="filter-section">
            <div className="filter-section-label">
              <i className="bi bi-currency-rupee" />
              Salary Range (₹)
              {hasValue(filters, ["salaryMin", "salaryMax"]) && <ActiveBadge />}
            </div>
            <div className="range-row">
              <div>
                <div className="range-label">Minimum</div>
                <input
                  type="number"
                  min="0"
                  className="filter-input"
                  value={filters.salaryMin}
                  onChange={(e) => onChange("salaryMin", e.target.value)}
                  placeholder="20,000"
                />
              </div>
              <div>
                <div className="range-label">Maximum</div>
                <input
                  type="number"
                  min="0"
                  className="filter-input"
                  value={filters.salaryMax}
                  onChange={(e) => onChange("salaryMax", e.target.value)}
                  placeholder="5,00,000"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="filter-footer">
            <button
              className="filter-apply-btn"
              type="submit"
              disabled={loading}
            >
              {loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                    aria-hidden="true"
                  />
                  Searching…
                </>
              ) : (
                <>
                  <i className="bi bi-search me-2" />
                  Apply Filters
                  {activeCount > 0 && (
                    <span
                      style={{
                        background: "rgba(255,255,255,0.25)",
                        borderRadius: 20,
                        padding: "1px 8px",
                        fontSize: 11,
                        marginLeft: 8,
                      }}
                    >
                      {activeCount}
                    </span>
                  )}
                </>
              )}
            </button>
          </div>
        </div>
      </form>
    </>
  );
};

export default JobFiltersPanel;
